import * as React from 'react';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';

import { FillInBlankQuestion, GRAMMAR, PlayFillInBlankQuestion } from '../../../Shared';
import { clearDisplayMessageAndError } from '../../actions/display';
import * as massEditActions from '../../actions/massEdit';
import {
  massEditDeleteResponses,
  submitMassEditConceptResults,
  submitMassEditFeedback
} from '../../actions/responses';
import { goToNextQuestion, startNewSession, storeResponseInAttempts } from '../../actions/session';
import { submitEditedQuestion } from '../../actions/fillInBlankQuestions';
import { ActionTypes } from '../../actions/actionTypes';
import { Match } from '../../interfaces/match';
import { getGradedResponsesWithCallback, saveResponseToCMS } from '../../actions/responses';
import FeedbackContainer from '../renderForQuestions/feedback';
import ResponseComponentWrapper from '../questions/responseRouteWrapper';
import { rematchFillInBlankResponsesForCue } from '../../libs/grading/rematchFillInBlankResponsesForCue';

// Use Grammar's native session actions
const clearData = () => startNewSession();

const loadData = (questions: any[]) => ({
  type: ActionTypes.RECEIVE_QUESTION_DATA,
  data: questions
});

const nextQuestion = () => goToNextQuestion();

interface FillInBlankQuestionWrapperProps extends RouteComponentProps {
  concepts: any;
  conceptsFeedback: any;
  dispatch: any;
  display: any;
  fillInBlank: any;
  massEdit: any;
  match: Match;
  session: any;
}

// Wrapper component to match Connect's pattern for FITB questions
const GrammarPlayFillInBlankQuestionWrapper = (props) => {
  const updateResponseResourceBound = (returnValue: any, questionID: string, attempts: any, dispatch: any) => {
    const latestAttempt = attempts ? attempts[attempts.length - 1] : undefined;
    const isFirstAttempt = !latestAttempt;
    dispatch(saveResponseToCMS(returnValue.response, isFirstAttempt));
  }

  const submitResponseBound = (response: any) => {
    const { dispatch } = props;
    dispatch(storeResponseInAttempts(response));
  }

  return (
    <div className="student-container student-container-diagnostic">
      <PlayFillInBlankQuestion
        {...props}
        FeedbackContainer={FeedbackContainer}
        getGradedResponsesWithCallback={getGradedResponsesWithCallback}
        isLastQuestion={false}
        mode={GRAMMAR}
        previewMode={true}
        submitResponse={submitResponseBound}
        updateResponseResource={updateResponseResourceBound}
      />
    </div>
  );
}

const GrammarTestComponent = ({ dispatch, fillInBlank, match, conceptsFeedback, concepts, session, }) => {
  React.useEffect(() => {
    initializeQuestion()

    // Cleanup function to clear session state when navigating away from the test page
    return () => {
      dispatch(clearData());
    }
  }, [match.params.questionID])

  const initializeQuestion = () => {
    const testQuestionID = match.params.questionID;
    const testQuestion = fillInBlank?.data?.[testQuestionID];

    if (testQuestion) {
      // Set up the question with empty attempts array and uid for session tracking
      const questionWithAttempts = { ...testQuestion, uid: testQuestionID, key: testQuestionID, attempts: [] };
      const questions = [questionWithAttempts];

      dispatch(clearData());
      dispatch(loadData(questions));
      dispatch(nextQuestion());
    }
  }

  const reset = () => {
    initializeQuestion();
  }

  const testQuestionID = match.params.questionID;
  const testQuestion = fillInBlank?.data?.[testQuestionID];
  const { currentQuestion } = session || {};

  if (!currentQuestion || !testQuestion) {
    return <p>Loading...</p>;
  }

  // Check if the currentQuestion matches the URL (using either uid or key)
  const currentQuestionID = currentQuestion.uid || currentQuestion.key;
  const questionMatches = currentQuestionID === testQuestionID;

  // Only merge session data if it's for the same question
  // This prevents old input values from persisting when switching questions
  const fullQuestion = {
    ...testQuestion,
    ...(questionMatches ? currentQuestion : {}),
    key: testQuestionID,
    attempts: questionMatches ? (currentQuestion.attempts || []) : []
  };

  return (
    <div className="test-question-container">
      <GrammarPlayFillInBlankQuestionWrapper
        concepts={concepts}
        conceptsFeedback={conceptsFeedback}
        dispatch={dispatch}
        nextQuestion={reset}
        question={fullQuestion}
      />
    </div>
  );
}

const FillInBlankQuestionWrapper: React.FC<FillInBlankQuestionWrapperProps> = ({
  concepts,
  conceptsFeedback,
  dispatch,
  display,
  fillInBlank,
  massEdit,
  match,
  session
}) => {
  const { questionID } = match.params;

  const question = fillInBlank.hasreceiveddata ? fillInBlank.data[questionID] : null;

  // Wrapper functions to adapt Grammar action signatures to FillInBlankQuestion interface
  const deleteAllWrapper = (responseIds: string[], qid: string) => {
    dispatch(massEditDeleteResponses(responseIds, qid));
  };

  const submitConceptResultsWrapper = (responseIds: string[], conceptResults: {[key: string]: boolean} | undefined, qid: string) => {
    dispatch(submitMassEditConceptResults(responseIds, conceptResults, qid));
  };

  const submitFeedbackWrapper = (responseIds: string[], payload: any, qid: string) => {
    dispatch(submitMassEditFeedback(responseIds, payload, qid));
  };

  const handleEditQuestion = (questionID: string, data: any) => {
    dispatch(submitEditedQuestion(questionID, data));
  };

  const handleEditBlankByBlankGrading = async (
    questionID: string,
    newBlankByBlankGrading: any,
    changeInfo?: { blankKey: string; cue: string }
  ) => {
    const updatedQuestion = {
      ...question,
      blankByBlankGrading: newBlankByBlankGrading
    };

    // 1. Submit the question update
    await dispatch(submitEditedQuestion(questionID, updatedQuestion));

    // 2. If we have change info, trigger auto-rematch
    // Use the updated question data directly to avoid race condition
    if (changeInfo) {
      triggerAutoRematch(questionID, updatedQuestion, changeInfo.blankKey, changeInfo.cue);
    }
  }

  const triggerAutoRematch = async (
    questionID: string,
    updatedQuestion: any,
    blankKey: string,
    cue: string
  ) => {
    try {
      const { succeeded } = await rematchFillInBlankResponsesForCue(
        questionID,
        updatedQuestion,
        blankKey,
        cue
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Auto-rematch failed:', error);
      // Silently fail - don't block the user's workflow
    }
  }

  const renderTestComponent = React.useCallback((routeProps: any) => (
    <GrammarTestComponent
      {...routeProps}
      concepts={concepts}
      conceptsFeedback={conceptsFeedback}
      dispatch={dispatch}
      fillInBlank={fillInBlank}
      session={session}
    />
  ), [concepts, conceptsFeedback, dispatch, fillInBlank, session]);

  return (
    <FillInBlankQuestion
      actions={{
        clearDisplayMessage: clearDisplayMessageAndError,
        clearResponses: massEditActions.clearResponsesFromMassEditArray,
        deleteAll: deleteAllWrapper,
        removeResponse: massEditActions.removeResponseFromMassEditArray,
        submitConceptResults: submitConceptResultsWrapper,
        submitFeedback: submitFeedbackWrapper
      }}
      basePath="fill-in-the-blanks"
      dispatch={dispatch}
      displayError={display?.error}
      displayMessage={display?.message}
      editBlankByBlankGrading={handleEditBlankByBlankGrading}
      editQuestionAction={handleEditQuestion}
      hasReceivedData={fillInBlank.hasreceiveddata}
      isGrammar={true}
      massEdit={massEdit}
      question={question}
      questionID={questionID}
      ResponseComponent={ResponseComponentWrapper}
      TestComponent={renderTestComponent}
      useRelativePaths={true}
    />
  );
}

function select(state: any) {
  return {
    conceptsFeedback: state.fillInBlankConceptsFeedback,
    concepts: state.concepts,
    display: state.display,
    fillInBlank: state.fillInBlank,
    massEdit: state.massEdit,
    session: state.session
  };
}

export default withRouter(connect(select)(FillInBlankQuestionWrapper));
